import {
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Separator,
  showNotification,
} from 'platform/components';
import {Grid, GridItem, VStack} from 'platform/foundation';
import {z} from 'zod';

import {VatType, VatTypeEnum, VatTypeSchema} from '@dms/api/shared';
import {useGetRentalPriceGroupQuery, useUpdateRentalPriceGroupMutation} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError, useVatRatesOptions} from '@dms/shared';

import {useRequiredParams} from 'shared/web';

import {PriceList} from './components/PriceList';

export type FormValues = {
  name: string;
  vatType: VatType;
};

export function PriceGroupsDetail() {
  const [vatTypeOptions] = useVatRatesOptions();

  const {id: rentalPriceGroupId} = useRequiredParams();

  const {data: priceGroup, isLoading, isError} = useGetRentalPriceGroupQuery({rentalPriceGroupId});
  const [updatePriceGroup] = useUpdateRentalPriceGroupMutation();

  const handleSubmit: FormSubmitHandler<FormValues> = async (values) =>
    await updatePriceGroup({
      rentalPriceGroupId,
      updateRentalPriceGroupRequestBody: values,
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));

        if (values.vatType !== priceGroup?.vatType) {
          showNotification.info(i18n.t('entity.rental.labels.vatRateChanged'));
        }
      })
      .catch(handleApiError);

  return (
    <SettingsTemplate
      header={{
        title: priceGroup?.name,
        breadcrumbs: [
          {label: i18n.t('entity.rental.labels.priceGroups'), href: settingsRoutes.priceGroupList},
        ],
      }}
      data-testid={testIds.settings.priceGroupList('page')}
      isLoading={isLoading}
      isError={isError}
    >
      <Form<FormValues>
        onSubmit={handleSubmit}
        defaultValues={{name: priceGroup?.name, vatType: priceGroup?.vatType}}
        experimentalZodSchema={schema}
        shouldWatchForUnsavedChanges
      >
        {(control, _formApi, navigateWithoutBlocker) => (
          <VStack spacing={6}>
            <SettingsSection>
              <Grid columns={2} align="flex-end">
                <FormField
                  control={control}
                  name="name"
                  type="text"
                  isRequired
                  label={i18n.t('general.labels.name')}
                />

                <FormField
                  control={control}
                  name="vatType"
                  type="choice"
                  isRequired
                  label={i18n.t('general.labels.vat')}
                  options={vatTypeOptions}
                  isNotClearable
                />

                <GridItem span={2}>
                  <FormButton
                    control={control}
                    type="submit"
                    title={i18n.t('general.labels.edit')}
                  />
                </GridItem>
              </Grid>
            </SettingsSection>

            <Separator spacing={0} />

            <PriceList
              items={priceGroup?.items ?? []}
              rentalPriceGroupId={rentalPriceGroupId}
              vatType={priceGroup?.vatType ?? VatTypeEnum.S}
            />

            <SettingsFooter
              actions={[
                {
                  type: 'button',
                  title: i18n.t('general.actions.close'),
                  onClick: () => navigateWithoutBlocker(settingsRoutes.priceGroupList),
                  variant: 'secondary',
                  'data-testid': testIds.settings.priceGroupDetail('close'),
                },
              ]}
            />
          </VStack>
        )}
      </Form>
    </SettingsTemplate>
  );
}

const schema = z.object({
  name: z.string().nonempty(),
  vatType: VatTypeSchema,
});
